import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, Trophy } from "lucide-react"
import { motion } from "framer-motion"
import { useInfiniteQuery } from "@tanstack/react-query"

import { RoundDetailSheet } from "@/features/history"

import { getPvpRouletteRoundHistory } from "@/shared/api"
import {
  chanceFractionToPercent,
  computeBetFromBankAndChance,
  computeRate,
  formatTonCompact,
} from "@/shared/lib/pvpRouletteMath"
import { cn } from "@/shared/lib/utils"
import { getAvatarInitials } from "@/shared/lib/avatarInitials"
import { resolveAvatarLoadUrl } from "@/shared/lib/avatarProxy"
import { formatPvpRoundDisplayDate } from "@/shared/lib/formatPvpRoundDisplayDate"
import { useI18n } from "@/shared/i18n"
import { useTelegramBackButton } from "@/shared/hooks/useTelegramBackButton"

import WinBadge from "@/assets/textures/win-badge.svg?react"
import RowTopShape from "@/assets/textures/win-history-top-badge.svg?react"
import RowBottomShape from "@/assets/textures/win-history-bottom-badge.svg?react"

const HistoryItemSkeleton = () => {
  return (
    <div className="animate-pulse overflow-hidden rounded-xl">
      <RowTopShape />
      <RowBottomShape />
    </div>
  )
}

export default function PvpRouletteHistory() {
  const { t, locale } = useI18n()

  const navigate = useNavigate()
  const useNativeBack = useTelegramBackButton(() => navigate(-1))

  const [onlyMyRounds, setOnlyMyRounds] = useState(false)

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["pvp-roulette-history", onlyMyRounds],
      queryFn: ({ pageParam }: { pageParam?: string }) =>
        getPvpRouletteRoundHistory({
          only_my_rounds: onlyMyRounds,
          limit: 10,
          cursor: pageParam,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
      staleTime: 30_000,
    })
  const [selectedRoundId, setSelectedRoundId] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const items = data?.pages.flatMap((p) => p.items) ?? []

  // Trigger next page fetch when sentinel enters viewport
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  return (
    <div className="mx-auto max-w-2xl px-4 py-4">
      {/* -mx-4: bleed bg into horizontal padding so scroll does not show through */}
      <div
        className={cn(
          "sticky top-0 z-50 -mx-4 mb-5 flex items-center justify-center bg-primary-main px-4",
          useNativeBack ? "py-2" : "relative min-h-12 py-1"
        )}
      >
        {!useNativeBack && (
          <button
            type="button"
            className="absolute left-0 flex size-12 items-center justify-center text-white"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
        )}
        <h1 className="text-base font-medium tracking-[-0.25px] text-white">
          {t("history.gamesHistoryTitle")}
        </h1>
      </div>

      <div className="flex h-14 w-full items-center justify-between gap-3 rounded-2xl bg-white/10 px-4">
        <span className="min-w-0 flex-1 text-base font-medium leading-[1.3] tracking-[-0.25px] text-[#F5F5F5]">
          {t("history.showMyGames")}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={onlyMyRounds}
          aria-label={t("history.showMyGames")}
          onClick={() => setOnlyMyRounds((prev) => !prev)}
          className={cn(
            "flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition-colors",
            onlyMyRounds ? "justify-end bg-success-100" : "justify-start bg-gray-100"
          )}
        >
          <span className="size-5 rounded-full bg-white shadow-[0_1px_3px_rgba(10,13,18,0.1),0_1px_2px_-1px_rgba(10,13,18,0.1)]" />
        </button>
      </div>

      <div className="flex flex-col pt-2 pb-4">
        <div
          className={cn(
            "sticky z-50 -mx-4 mb-2 grid min-h-[47px] grid-cols-[1.6fr_0.45fr_0.45fr_0.45fr_0.65fr] items-center gap-1 bg-primary-main px-7 text-xs/none leading-[15px] tracking-[-0.5px] text-white/60",
            useNativeBack ? "top-10" : "top-12"
          )}
        >
          <span>{t("history.winner")}</span>
          <span className="text-center">%</span>
          <span className="text-center">{t("history.bet")}</span>
          <span className="text-center">{t("history.prize")}</span>
          <span className="text-right">{t("history.rate")}</span>
        </div>

        {/* Content */}
        {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <HistoryItemSkeleton key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center gap-3 text-white/60">
          <Trophy className="h-10 w-10 opacity-30" />
          <p className="text-sm">{t("history.noRounds")}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((item, index) => {
            const wp = item.winner_party
            const isTeam = wp?.type === "TEAM"
            const winners = wp?.winners ?? []
            const winChance = wp?.win_chance ?? 0
            const prizeNano = wp?.gross_payout_amount ?? 0
            const prizeTon = prizeNano / 1e9
            const totalBankTon = (item.total_bank ?? 0) / 1e9
            const betTon = computeBetFromBankAndChance(totalBankTon, winChance)
            const rate = computeRate(prizeTon, betTon)
            const winPercent = chanceFractionToPercent(winChance)

            const displayName = isTeam
              ? undefined
              : (winners[0]?.username ?? t("history.unknown"))

            return (
              <motion.div
                key={item.round_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index, 9) * 0.03 }}
              >
                <div
                  className="cursor-pointer overflow-hidden transition-transform active:scale-[0.98]"
                  onClick={() => setSelectedRoundId(item.round_id)}
                >
                  {/* Main row — lighter (contrast vs `bg-background` / --ds-page-background) */}
                  <div className="relative grid grid-cols-[1.6fr_0.45fr_0.45fr_0.45fr_0.65fr] items-center gap-1 border-b border-white/40 px-3 pb-2.5 pt-3">
                    <RowTopShape className="absolute inset-0 h-full w-full" />
                    {/* Winner */}
                    <div className="relative flex min-w-0 items-center gap-1">
                      {isTeam ? (
                        <div className="flex items-center">
                          {winners.slice(0, 5).map((w, i) => (
                            <div
                              key={w.user_id}
                              className={cn(
                                "flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white bg-white/20 text-[8px] font-bold text-white",
                                i > 0 && "-ml-2"
                              )}
                            >
                              {w.photo_url ? (
                                <img
                                  src={resolveAvatarLoadUrl(w.photo_url)}
                                  alt={w.username ?? ""}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                getAvatarInitials(w.username, "?", 1)
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <>
                          <div className="flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/20 text-xs font-bold text-white">
                            {winners[0]?.photo_url ? (
                              <img
                                src={resolveAvatarLoadUrl(winners[0].photo_url)}
                                alt={displayName ?? ""}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              getAvatarInitials(displayName, "?", 1)
                            )}
                          </div>
                          <span className="truncate text-sm font-medium text-white">
                            {displayName}
                          </span>
                        </>
                      )}
                    </div>

                    {/* % */}
                    <span className="relative text-center text-sm font-medium text-white">
                      {winChance > 0 ? `${Math.round(winPercent)}%` : "—"}
                    </span>

                    {/* Bet */}
                    <span className="relative text-center text-sm font-medium text-white">
                      {formatTonCompact(betTon)}
                    </span>

                    {/* Prize */}
                    <span className="relative text-center text-sm font-medium text-white">
                      {formatTonCompact(prizeTon)}
                    </span>

                    {/* Rate badge */}
                    <div className="relative flex justify-end">
                      <div
                        className={cn(
                          "relative rounded-lg px-3 py-1.5 text-sm font-bold text-[#7BFF00]",
                          rate >= 6 && "text-[#E5FF00]",
                          rate >= 11 && "text-[#FFB200]",
                          rate >= 16 && "text-[#FF6200]",
                          rate >= 21 && "text-[#FF0000]"
                        )}
                      >
                        <WinBadge
                          className={cn(
                            "absolute inset-0 h-full w-full drop-shadow-[0_1px_0_rgb(0,0,0)]"
                          )}
                        />
                        <span className="relative text-white [text-shadow:_0.5px_0_0.5px_black,_-0.5px_0_0.5px_black,_0px_-0.5px_0.5px_black,_0px_1px_0.5px_black]">
                          x{rate >= 10 ? rate.toFixed(0) : rate.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Footer — darker */}
                  <div className="relative flex items-center justify-between px-3 py-1.5">
                    <RowBottomShape className="absolute inset-0 h-full w-full" />
                    <span className="relative z-[1] text-[10px] leading-3 tracking-[-0.5px] text-white/60">
                      #{item.number}
                    </span>
                    <span className="relative z-[1] shrink-0 text-[10px] leading-3 tracking-[-0.5px] text-white/60">
                      {formatPvpRoundDisplayDate(item.created_at, locale, t)}
                    </span>
                  </div>
                </div>
              </motion.div>
            )
          })}

          {/* Skeleton placeholders while fetching next page */}
          {isFetchingNextPage && (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <HistoryItemSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Invisible sentinel for IntersectionObserver */}
          <div ref={sentinelRef} className="h-4" />
        </div>
        )}
      </div>

      <RoundDetailSheet
        roundId={selectedRoundId}
        onClose={() => setSelectedRoundId(null)}
      />
    </div>
  )
}
